import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { InputOptions } from 'types/input'
import { formatDate } from 'utils/helpers'

export const FILTER_FIELD_PRICE_LABEL_SYNC_HISTORY = 'priceLabelSyncFilters'

// Seen / Unseen filter. `NONE` clears the filter (handled by useLogFilters);
// the other values are sent to the server verbatim as `isSeen=true|false`.
export const SEEN_OPTIONS: InputOptions[] = [
  { label: '--- NONE ---', value: 'NONE' },
  { label: 'Seen', value: 'true' },
  { label: 'Unseen', value: 'false' },
]

const baseColumns: ColDef[] = [
  {
    headerName: 'Barcode',
    field: 'barcode',
    minWidth: 140,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    // `store` is the store code, not the name.
    headerName: 'Store',
    field: 'store',
    minWidth: 130,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: 'Aisle',
    field: 'aisle',
    minWidth: 100,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: 'Shelf',
    field: 'shelf',
    minWidth: 100,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    // `facing` can be null.
    headerName: 'Facing',
    field: 'facing',
    minWidth: 100,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: 'Requested By',
    field: 'requestedBy.name',
    minWidth: 160,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: 'Emp ID',
    field: 'requestedBy.empId',
    minWidth: 120,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: 'Status',
    field: 'isSeen',
    minWidth: 120,
    cellRenderer: (params: ICellRendererParams) => {
      const isSeen: boolean = params.value
      return (
        <span
          className={`rounded-lg px-2 py-1 text-sm ${
            isSeen ? 'bg-green-400 text-black' : 'bg-gray-200 text-gray-700'
          }`}
        >
          {isSeen ? 'Seen' : 'Unseen'}
        </span>
      )
    },
  },
  {
    headerName: 'Synced At',
    field: 'createdAt',
    minWidth: 180,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? formatDate(params.value, { includeTime: true }) : '-',
  },
]

// Leading checkbox column for the bulk "mark as read" action. Only unseen rows
// are selectable (seen ones show a disabled checkbox); the header checkbox
// selects every unseen row on the current page.
const selectionColumn: ColDef = {
  colId: 'select',
  headerName: '',
  maxWidth: 56,
  minWidth: 56,
  pinned: 'left',
  sortable: false,
  checkboxSelection: true,
  headerCheckboxSelection: true,
  headerCheckboxSelectionFilteredOnly: true,
  showDisabledCheckboxes: true,
}

// Editors get the selection column prepended so they can bulk mark rows as read;
// view-only users see the plain listing.
export const getColumns = (isEditor: boolean): ColDef[] =>
  isEditor ? [selectionColumn, ...baseColumns] : baseColumns
